import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { EntityState, serializeAxiosError, IQueryParams } from 'app/shared/reducers/reducer.utils';
import { IIndicatorDto, defaultValue } from 'app/shared/model/indicator-dto.model';
import { PeriodicType } from 'app/shared/model/enumerations/periodicType';
import { IKpiResponse, IKpiTimeResponse } from 'app/shared/model/indicator-dto';

const initialState: EntityState<IIndicatorDto> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};
export interface IDistanceTraveledProps extends IQueryParams {
  sensor: string | null;
  periodicType: PeriodicType | null;
  truckImplementId?: number | string;
  companyId?: number | null;
  dateStart?: string | null;
  dateEnd?: string | null;
}

const apiUrl = '/api/indicator-difference';

// Actions

export const getDistanceTraveledPerTime = createAsyncThunk(
  'distanceTraveled/fetch_distance_traveled_per_time_periodic',
  async ({ sensor, periodicType, truckImplementId, companyId, dateStart, dateEnd }: IDistanceTraveledProps) => {
    const apiUrlComplete = `${apiUrl}/time/${sensor}${truckImplementId ? `/${truckImplementId}` : ''}/${periodicType}${
      companyId ? `?searchCompanyId=${companyId}` : ''
    }`;
    const params = {
      dateStart,
      dateEnd,
    };
    const response = await axios.get<IKpiTimeResponse>(apiUrlComplete, { params });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const getDistancePerVehicleAndPlate = createAsyncThunk(
  'distanceTraveled/fetch_distance_per_vehicle_and_plate_periodic',
  async ({ sensor, periodicType, companyId, dateStart, dateEnd }: IDistanceTraveledProps) => {
    const apiUrlComplete = `${apiUrl}/statistics/${sensor}/${periodicType}${companyId ? `?searchCompanyId=${companyId}` : ''}`;
    const params = {
      dateStart,
      dateEnd,
    };
    const response = await axios.get<IKpiResponse>(apiUrlComplete, { params });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

// Slice

const distanceTraveledSlice = createSlice({
  name: 'distanceTraveled',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getDistanceTraveledPerTime.fulfilled, (state, action) => {
        state.loading = false;
        state.entity.kpiPerTime = action.payload;
      })
      .addCase(getDistanceTraveledPerTime.pending, state => {
        state.loading = true;
      })
      .addCase(getDistanceTraveledPerTime.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching data';
      })
      .addCase(getDistancePerVehicleAndPlate.fulfilled, (state, action) => {
        state.loading = false;
        state.entity.kpiPerVehicleAndStatistics = action.payload;
      })
      .addCase(getDistancePerVehicleAndPlate.pending, state => {
        state.loading = true;
      })
      .addCase(getDistancePerVehicleAndPlate.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching data';
      });
  },
});

export const { reset } = distanceTraveledSlice.actions;

export default distanceTraveledSlice.reducer;
